import { useLocationSearch } from '@rocketlane/rocket-router';
import {
  ProjectId,
  ProjectConversationId,
  SpaceId,
  SpaceTabId,
  TemplateId,
  FormInstanceId,
} from 'api/models';
import { ProjectGlobalConversation } from 'api/project-conversations';
import { ReportType } from 'components/pages/custom-reports/types';
import { CDN_BASE_URL } from 'config/environment';
import { RoutePaths } from 'constant';
import { TemplateObjectType } from 'data/templates/api';
import { ROUTE_PATHS, type RoutePathsType } from 'routes/routes-paths';

export const getSpaceDetailUrl = ([, projectId, spaceId]: SpaceId): string => {
  return `/projects/${projectId}/spaces/${spaceId}`;
};

export const getSpaceTabDetailUrl = <T>([
  ,
  projectId,
  spaceId,
  spaceTabId,
]: SpaceTabId<T>): string => {
  return `/projects/${projectId}/spaces/${spaceId}/tabs/${spaceTabId}/`;
};

const ProjectsPrefix = '/projects';

export const getProjectDetailUrl = ([, projectId]: ProjectId): string => {
  return RoutePaths.projectDetail.replace(/(:\w+\b)/, projectId.toString());
};

export const getProjectTasksUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/tasks`;
};

export const getProjectFormResponseUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/forms/responses`;
};

export const getProjectOverviewUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/overview`;
};
export const getProjectAskAiUrl = (
  projectId: ProjectId,
  conversationId?: string,
): string => {
  const baseUrl = `${getProjectDetailUrl(projectId)}/${ROUTE_PATHS.ASK_AI}`;

  if (conversationId) {
    return `${baseUrl}/${conversationId}`;
  }

  return baseUrl;
};
export const getProjectFinancialsUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/financials`;
};
export const getProjectPlanUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/plan`;
};

export const getProjectConversationPageUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/chat`;
};

export const getProjectSpacesUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/spaces`;
};

export const getProjectTimesheetUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/timesheet`;
};

export const getProjectInvoicesUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/invoices`;
};

export const getProjectExpenseUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/expense`;
};

export const getProjectExpenseReportUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/expense-report`;
};

export const getProjectConversationDetailUrl = (
  projectId: ProjectId,
  chatId: ProjectConversationId,
): string => {
  return `${getProjectDetailUrl(projectId)}/chat/${chatId[1]}`;
};

export const GlobalConversationSeparator = ':';

export const getProjectGlobalConversationDetailUrl = (
  projectId: ProjectId,
  entityId: ProjectGlobalConversation['entityId'],
  entityType: ProjectGlobalConversation['entityType'],
): string => {
  return `${getProjectDetailUrl(
    projectId,
  )}/chat/${entityType}${GlobalConversationSeparator}${entityId}`;
};

export const getProjectStatusUpdatesUrl = (projectId: ProjectId): string =>
  `${getProjectDetailUrl(projectId)}/status-updates`;

export const getProjectTaskListUrl = (projectId: ProjectId): string => {
  return `${getProjectTasksUrl(projectId)}/list`;
};

export const getProjectTaskBoardUrl = (projectId: ProjectId): string => {
  return `${getProjectTasksUrl(projectId)}/board`;
};

export const getProjectSettingsUrl = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/settings`;
};

export const getProjectFinancialsSettingsUrl = ([
  ,
  projectId,
]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/settings/budget-management`;
};

export const getProjectFormResponsesUrl = ([
  ,
  projectId,
]: ProjectId): string => {
  return RoutePaths.projectFormResponses.replace(
    ':projectId',
    projectId.toString(),
  );
};

export const getProjectFormByInstanceUrl = ([
  ,
  projectId,
  instanceId,
]: FormInstanceId): string => {
  return RoutePaths.projectFormResponseByInstance
    .replace(':projectId', projectId.toString())
    .replace(':instanceId', instanceId.toString());
};

export const getProjectFilesUrl = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/files`;
};

export const getProjectSheetsUrl = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/sheets`;
};

export const getProjectNitroPagesUrl = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/nitro-pages`;
};

export const getProjectEmailPagesUrl = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/email-pages`;
};

export const getProjectMeetingPagesUrl = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/meeting-pages`;
};

export const getProjectSignalsV2Url = ([, projectId]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/signals-v2`;
};

export const getProjectCustomerPortalConfigurationUrl = ([
  ,
  projectId,
]: ProjectId): string => {
  return `${ProjectsPrefix}/${projectId}/customer-portal-configuration`;
};

const TemplatesPrefix = '/templates';

const templateTypePrefixMap = {
  [TemplateObjectType.Project]: 'projects',
  [TemplateObjectType.Document]: 'documents',
  [TemplateObjectType.StatusUpdates]: 'status-updates',
  [TemplateObjectType.Form]: 'forms',
};

export const getTemplateUrl = (
  [, templateId]: TemplateId,
  type: TemplateObjectType,
): string => {
  const typePrefix = templateTypePrefixMap[type];

  return `${TemplatesPrefix}/${typePrefix}/${templateId}`;
};

export const getSheetTemplateUrl = ([, templateId]: TemplateId): string => {
  return `${TemplatesPrefix}/sheet/${templateId}`;
};

export const getTaskFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editTaskField.replace(':fieldId', fieldId.toString());
};

export const getProjectFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editProjectField.replace(':fieldId', fieldId.toString());
};

export const getTeamMemberFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editTeamMemberField.replace(':fieldId', fieldId.toString());
};

export const getSpintFieldEditUrl = (fieldId: number): string => {
  return RoutePaths.editSprintField.replace(':fieldId', fieldId.toString());
};

export const getEpicFieldEditUrl = (fieldId: number): string => {
  return RoutePaths.editEpicField.replace(':fieldId', fieldId.toString());
};

export const getInvoiceFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editInvoiceField.replace(':fieldId', fieldId.toString());
};

export const getTimeEntryFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editTimeEntryField.replace(':fieldId', fieldId.toString());
};

export const getExpenseFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editExpenseField.replace(':fieldId', fieldId.toString());
};

export const getAllocationFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editAllocationField.replace(':fieldId', fieldId.toString());
};

export const getCustomerTeamMemberFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editCustomerTeamMemberField.replace(
    ':fieldId',
    fieldId.toString(),
  );
};

export const getCompanyFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editCompanyField.replace(':fieldId', fieldId.toString());
};
export const getSkillFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editSkillField.replace(':fieldId', fieldId.toString());
};

export const getKeyeventFieldEditUrl = (fieldId: number): string => {
  return RoutePaths.editKeyevent.replace(':eventId', fieldId.toString());
};

export const getIntervalFieldEditUrl = (fieldId: number): string => {
  return RoutePaths.editInterval.replace(':intervalId', fieldId.toString());
};

export const getBudgetFieldEditUrl = (fieldId: number): string => {
  /* TODO:: can be replaced with regex: (:\w+\b) */
  return RoutePaths.editBudgetField.replace(':fieldId', fieldId.toString());
};

export const getProjectStatusUpdatesDetailUrl = (
  projectId: number | string,
  statusUpdateId: number | string,
): string => {
  return `/projects/${projectId}/status-updates/${statusUpdateId}`;
};

// https://regex101.com/r/sIQoje/1
export const URlRegEx = new RegExp(
  '^(https?://)?(www.)?[a-z0-9]{1,}([-_.]{1}[a-z0-9]+)*([.][a-z]{2,64})(/{1}[a-z0-9]{1,}[a-z0-9%#_.?&~=*+-]*)*/?$',
  'ig',
);

export const getNewWidgetUrl = (dashboardId: number, search = ''): string => {
  return `${RoutePaths.newWidget.replace(
    ':dashboardId',
    dashboardId.toString(),
  )}${search}`;
};

export const getDashboardUrl = (dashboardId: number): string => {
  return `/dashboards/${dashboardId}`;
};

export const getEditWidgetUrl = (
  dashboardId: number,
  widgetId: number,
): string => `widgets/${widgetId}/edit`;

export const getEditSheetWidgetUrl = (
  sheetTemplateId: number,
  widgetId: number,
): string => `overview/widgets/${widgetId}/edit-card`;

// use this dashboard url utils because the default utils overrides the query params of global filters
export const useGetDashboardUrlWithSearch = () => {
  const search = useLocationSearch();

  return (dashboardId: number) => `${getDashboardUrl(dashboardId)}${search}`;
};

export const useGetEditWidgetUrlWithSearch = () => {
  const search = useLocationSearch();

  return (dashboardId: number, widgetId: number) =>
    `${getEditWidgetUrl(dashboardId, widgetId)}${search}`;
};

export const useGetEditSheetWidgetUrlWithSearch = () => {
  const search = useLocationSearch();

  return (sheetTemplateId: number, widgetId: number) =>
    `${getEditSheetWidgetUrl(sheetTemplateId, widgetId)}${search}`;
};

export const getProjectPreviewUrl = (projectId: ProjectId): string => {
  return `${getProjectDetailUrl(projectId)}/preview`;
};

export const getSalesforceBaseUrl = (integrationId: string): string =>
  `${RoutePaths.salesforceIntegrationById.replace(
    ':integrationId',
    integrationId,
  )}`;
export const getSalesforceAutomationUrl = (integrationId: string): string =>
  `${getSalesforceBaseUrl(integrationId)}/automations`;
export const getSalesforceNewAutomationUrl = (integrationId: string): string =>
  `${getSalesforceBaseUrl(integrationId)}/automations/new`;
export const getSalesforceMappingsUrl = (integrationId: string): string =>
  `${getSalesforceBaseUrl(integrationId)}/resource-mappings`;
export const getSalesforceSettingsUrl = (integrationId: string): string =>
  `${getSalesforceBaseUrl(integrationId)}/settings`;
export const getSalesforceErrorLogsUrl = (integrationId: string): string =>
  `${getSalesforceBaseUrl(integrationId)}/error-logs`;
export const getSalesforceAutomationEditUrl = (
  integrationId: string,
  automationId: number,
): string =>
  `${getSalesforceBaseUrl(integrationId)}/automations/${automationId}/edit`;

export const getHubspotBaseUrl = (integrationId: string): string =>
  `${RoutePaths.hubspotIntegrationById.replace(
    ':integrationId',
    integrationId,
  )}`;

export const getHubspotSettingsUrl = (integrationId: string): string =>
  `${getHubspotBaseUrl(integrationId)}/settings`;
export const getHubspotMappingsUrl = (integrationId: string): string =>
  `${getHubspotBaseUrl(integrationId)}/resource-mappings`;

export const stripSlashes = (url: string): string => {
  // url should be single path name like `/templates/` not `/parent/child/`.
  return url
    .split('/')
    .filter((i) => i !== '/')
    .join('');
};

export const getCustomerPreviewUrl = (path: string) => `${path}`;

export const getFormResponsetaskDrawerurl = (
  projectId: number,
  taskId: number,
  answerId: number,
) =>
  `#task_drawer=${projectId}.${taskId}&task_drawer_tab=conversations&form_response_id=${answerId}`;

export const getCustomReportsUrl = (
  customReportId: number | string,
  customReportType: ReportType,
  customReportQueryParams?: string,
): string =>
  `${RoutePaths.customReport
    .replace(':customReportType', customReportType)
    .replace(':customReportId', customReportId.toString())}${
    customReportQueryParams || ''
  }`;

export const buildChildRouteUrl = (
  ...segments: (RoutePathsType | string)[]
): string => {
  return segments
    .join('/')
    .replace(/\/+/g, '/') // Replace multiple slashes with a single slash
    .replace(/\/$/, '') // Remove trailing slash if any
    .replace(/^(.+)?$/, '/$1'); // Ensure the URL starts with a single slash
};

export const ASSETS_FOLDER_PROD = 'rocketlane-attachments-public';
export const ASSETS_FOLDER_STAGING = 'rocketlane-attachments-public-stage';

export const isCdnUrl = (url: string) => {
  return url.startsWith(CDN_BASE_URL);
};

export const replaceWithCDNUrl = (oldUrl: string) => {
  if (
    isCdnUrl(oldUrl) ||
    (!oldUrl.includes(ASSETS_FOLDER_PROD) &&
      !oldUrl.includes(ASSETS_FOLDER_STAGING))
  ) {
    return oldUrl;
  }
  const link = document.createElement('a');

  link.href = oldUrl;

  // Extract the path segments
  //* https://s3.us-east-1.amazonaws.com/rocketlane-attachments-public-stage/4337/3ebbc361-7f63-4d63-8cd9-688800bd5718/sysdig-2.png -> 4337/3ebbc361-7f63-4d63-8cd9-688800bd5718/sysdig-2.png
  const pathSegments = link.pathname.split('/').slice(2).join('/');

  return `${CDN_BASE_URL}/${pathSegments}`;
};

export const getProjectTemplateSettingsUrl = (
  templateId: number | string,
): string => {
  return RoutePaths.projectTemplateSettings.replaceAll(
    ':templateId',
    templateId.toString(),
  );
};

export const getProjectFinancialRateCardSettingsUrl = (
  projectId: ProjectId,
): string => {
  return `${getProjectDetailUrl(projectId)}/settings/rate-cards`;
};

export const getProjectFinancialDefaultConfigurationSettingsUrl = (
  projectId: ProjectId,
): string => {
  return `${getProjectDetailUrl(projectId)}/settings/default-configuration`;
};

export const getProjectFinancialBudgetManagementSettingsUrl = (
  projectId: ProjectId,
  selectedBudgetId?: number,
): string => {
  return `${getProjectDetailUrl(projectId)}/settings/budget-management${
    selectedBudgetId ? `/${selectedBudgetId}` : ''
  }`;
};
